<template>
  <div class="filters-selected">
    <div v-if="selectedClients.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.ClientPlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="client in selectedClients" :key="client.ID" type="is-primary is-small is-icon" :value="client.ID" @click="onSelectedClientRemove(client.ID)">
            <span>{{ client.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedCampaigns.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Campaigns.Title") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="campaign in selectedCampaigns" :key="campaign.ChannelId" type="is-primary is-small is-icon" :value="campaign.ChannelId" @click="onSelectedCampaignRemove(campaign.ChannelId)">
            <span>{{ campaign.ChannelTitle }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedProjects.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.ProjectPerformancePlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="project in selectedProjects" :key="project.ID" type="is-primary is-small is-icon" :value="project.ID" @click="onSelectedProjectRemove(project.ID)">
            <span>{{ project.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedChannelMetadataTypes.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.ChannelMetadataPlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button
            v-for="channelMetadataType in selectedChannelMetadataTypes"
            :key="channelMetadataType.ID"
            type="is-primary is-small is-icon"
            :value="channelMetadataType.ID"
            @click="onSelectedChannelMetadataTypeRemove(channelMetadataType.ID)"
          >
            <span>{{ channelMetadataType.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <b-button type="is-warning" size="is-small" class="reset-filters-btn" :label="$t('ResetFilters')"
          v-if="hasFilters"
          @click="resetFilters()" />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { MultiselectItem } from "@/models/MultiselectItem";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignGridView } from "@/models/campaigns/CampaignGridView";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { AvailableChannelMetadataTypesMode } from "@/models/campaigns/ChannelMetadataType";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import IconBase from "../IconBase.vue";
import IconTimesSmall from "../icons/IconTimesSmall.vue";

const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconTimesSmall,
  },
})
export default class CampaignsFiltersSelected extends Vue {
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("campaignsWithStats", { namespace }) campaignsWithStats!: CampaignGridView[];
  @Getter("clients", { namespace }) clients!: MultiselectItem[];
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("isMediaChannel", { namespace }) isMediaChannel!: boolean;
  @Getter("projects", { namespace }) projects!: MultiselectItem[];
  @Getter("selectedCampaignsIds", { namespace }) selectedCampaignsIds!: number[];
  @Getter("selectedCampaignStatus", { namespace }) selectedCampaignStatus!: CampaignStatus | null;
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedClientsIds", { namespace }) selectedClientsIds!: number[];
  @Getter("selectedProjectsIds", { namespace }) selectedProjectsIds!: number[];
  @Action("setSelectedCampaignsIds", { namespace }) setSelectedCampaignsIds!: (selectedCampaignsIds: number[]) => void;
  @Action("setSelectedChannelMetadataTypesIds", { namespace }) setSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setSelectedClientsIds", { namespace }) setSelectedClientsIds!: (selectedClientsIds: number[]) => void;
  @Action("setSelectedProjectsIds", { namespace }) setSelectedProjectsIds!: (selectedProjectsIds: number[]) => void;
  
  get hasFilters(): boolean {
    return this.selectedCampaignsIds.length > 0
          || this.selectedClients.length > 0
          || this.selectedChannelMetadataTypes.length > 0
          || this.selectedProjectsIds.length > 0
          || !HelperService.compareDateArrays(this.datePeriod, HelperService.getThisMonthPeriod())
          || this.selectedCampaignStatus !== null && this.selectedCampaignStatus !== CampaignStatus.Active;
  }

  get selectedCampaigns(): CampaignGridView[] {
    if (!this.campaignsWithStats) {
      return [];
    }
    return this.campaignsWithStats.filter((i: CampaignGridView) => this.selectedCampaignsIds.indexOf((i.ChannelId as any) as number) >= 0);
  }

  get selectedChannelMetadataTypes(): MultiselectItem[] {
    const mode: AvailableChannelMetadataTypesMode = this.isMediaChannel ? AvailableChannelMetadataTypesMode.MediaCampaigns : AvailableChannelMetadataTypesMode.Campaigns;
    return CampaignService.getChannelMetadataTypes(mode).filter((i: MultiselectItem) => this.selectedChannelMetadataTypesIds.indexOf(+i.ID) >= 0);
  }

  get selectedClients(): MultiselectItem[] {
    return this.clients.filter((i: MultiselectItem) => this.selectedClientsIds.indexOf((i.ID as any) as number) >= 0);
  }

  get selectedProjects(): MultiselectItem[] {
    return this.projects.filter((i: MultiselectItem) => this.selectedProjectsIds.indexOf((i.ID as any) as number) >= 0);
  }

  onSelectedCampaignRemove(selectedCampaignId: number): void {
    const selectedCampaignsIdsNew: number[] = this.selectedCampaignsIds.filter((i) => i !== selectedCampaignId);
    this.setSelectedCampaignsIds(selectedCampaignsIdsNew);
  }

  onSelectedChannelMetadataTypeRemove(selectedChannelMetadataTypeId: number): void {
    const selectedChannelMetadataTypesIdsNew: number[] = this.selectedChannelMetadataTypesIds.filter((i) => i !== +selectedChannelMetadataTypeId);
    this.setSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIdsNew);
  }

  onSelectedClientRemove(selectedClientsId: number): void {
    const selectedClientsIdsNew: number[] = this.selectedClientsIds.filter((i) => i !== selectedClientsId);
    this.setSelectedClientsIds(selectedClientsIdsNew);
  }

  onSelectedProjectRemove(selectedProjectId: number): void {
    const selectedProjectsIdsNew: number[] = this.selectedProjectsIds.filter((i) => i !== selectedProjectId);
    this.setSelectedProjectsIds(selectedProjectsIdsNew);
  }

  resetFilters(): void {
    CampaignService.resetFilters(this.campaignsPageData.ClientId !== 0);
  }
}
</script>
